import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Fade,
  Skeleton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { formatUnifiedPrice, getListingCurrency } from '../../utils/priceCalculatorUnified';

// Fonction d'adaptation pour convertir la nouvelle structure unifiée
// vers l'ancienne structure attendue par ce composant
const adaptUnifiedCalculation = (unifiedCalc) => {
  if (!unifiedCalc) return null;
  
  if (!unifiedCalc.isValid) {
    return unifiedCalc; // Garde les erreurs telles quelles
  }

  console.log('🔍 [RealTimePricing] Adaptation du calcul unifié:', unifiedCalc);

  // Adapter la structure vers l'ancien format
  return {
    isValid: unifiedCalc.isValid,
    currency: unifiedCalc.currency,
    method: unifiedCalc.display?.method || 'daily',
    
    // Durée
    duration: {
      totalDays: unifiedCalc.raw?.days || unifiedCalc.breakdown?.days || 0,
    },
    
    // Prix détaillés
    pricing: {
      subtotal: unifiedCalc.raw?.rental_amount || 0,
      serviceFee: unifiedCalc.raw?.service_fee || 0,
      tax: unifiedCalc.raw?.tax_amount || 0,
      subtotalWithFees: (unifiedCalc.raw?.rental_amount || 0) + (unifiedCalc.raw?.service_fee || 0),
      deposit: unifiedCalc.raw?.deposit_amount || 0,
      total: unifiedCalc.raw?.total_with_deposit || unifiedCalc.breakdown?.total_amount || 0
    },
    
    // Répartition détaillée  
    breakdown: {
      method: unifiedCalc.display?.method || 'daily',
      description: unifiedCalc.display?.method || 'Tarif journalier'
    }
  };
};

// Fonction de description de prix adaptée
const getUnifiedPriceDescription = (adaptedCalc) => {
  if (!adaptedCalc || !adaptedCalc.duration) return 'Tarif non disponible';
  
  const days = adaptedCalc.duration.totalDays;
  const method = adaptedCalc.method || adaptedCalc.breakdown?.method || 'daily';
  
  if (method === 'daily') {
    return `Tarif journalier (${days} jour${days > 1 ? 's' : ''})`;
  } else if (method === 'weekly') {
    return `Tarif hebdomadaire`;
  } else if (method === 'monthly') {
    return `Tarif mensuel`;
  } else {
    return `Tarif ${method}`;
  }
};

const RealTimePricing = ({ 
  calculation, 
  isLoading = false, 
  startDate, 
  endDate, 
  showDetails = true 
}) => {
  const { t } = useTranslation('booking');
  
  // Adapter le calcul unifié vers l'ancienne structure
  const adaptedCalculation = adaptUnifiedCalculation(calculation);
  
  console.log('🔍 [RealTimePricing] Input calculation:', calculation);
  console.log('🔍 [RealTimePricing] Adapted calculation:', adaptedCalculation);
  
  if (isLoading) {
    return (
      <Card sx={{ 
        mb: 1,
        borderRadius: '0 0 24px 24px',
        borderTop: 'none',
        mt: 0
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CalculateIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {t('realTimePricing.title')}
            </Typography>
          </Box>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!adaptedCalculation || (!startDate || !endDate)) {
    return (
      <Card sx={{ 
        mb: 1, 
        opacity: 0.6,
        borderRadius: '0 0 24px 24px',
        borderTop: 'none',
        mt: 0
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CalculateIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              {t('realTimePricing.title')}
            </Typography>
          </Box>
          <Typography color="text.secondary">
            {t('realTimePricing.selectPeriod')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!adaptedCalculation.isValid) {
    return (
      <Card sx={{ 
        mb: 1,
        borderRadius: '0 0 24px 24px',
        borderTop: 'none',
        mt: 0
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ErrorIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6" color="error">
              Erreur de Calcul
            </Typography>
          </Box>
          <Alert severity="error" variant="outlined">
            {adaptedCalculation.error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const { pricing, duration, breakdown, method } = adaptedCalculation;

  // 🔍 LOG DEBUG: Valeur de la caution dans RealTimePricing
  console.log('🔍 DEBUG CAUTION RealTimePricing:', {
    'pricing.deposit': pricing.deposit,
    'pricing.total': pricing.total,
    'pricing object': pricing,
    'calculation object': calculation
  });

  return (
    <Fade in timeout={300}>
      <Card sx={{ 
        mb: 1, 
        border: '2px solid', 
        borderColor: '#e3f2fd',
        borderRadius: '0 0 24px 24px',
        borderTop: 'none',
        mt: 0
      }}>
        <CardContent>
          {/* Durée sélectionnée */}
          <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
             
            <Typography variant="body1" fontWeight="medium">
              {startDate?.toLocaleDateString('fr-FR')} - {endDate?.toLocaleDateString('fr-FR')} 
              ({duration.totalDays} jour{duration.totalDays > 1 ? 's' : ''})
            </Typography>
          </Box>

          {/* Prix principal */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('realTimePricing.estimatedTotal')}
            </Typography>
            <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
              {formatUnifiedPrice(pricing.total, adaptedCalculation.currency)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getUnifiedPriceDescription(adaptedCalculation)}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Détails du calcul */}
          {showDetails && (
            <Accordion variant="outlined" sx={{ boxShadow: 'none' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="price-breakdown"
                id="price-breakdown-header"
              >
                <Typography variant="body2" fontWeight="medium">
                  {t('realTimePricing.calculationDetails')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Box>
                  {/* Sous-total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {t('realTimePricing.breakdown.subtotal')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatUnifiedPrice(pricing.subtotal, adaptedCalculation.currency)}
                    </Typography>
                  </Box>

                  {/* Frais de service */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('realTimePricing.breakdown.serviceFee')} ({pricing.serviceFeePercent}%):
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatUnifiedPrice(pricing.serviceFee, adaptedCalculation.currency)}
                    </Typography>
                  </Box>

                  {/* TVA */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('realTimePricing.breakdown.tax')} ({pricing.taxPercent}%):
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatUnifiedPrice(pricing.tax, adaptedCalculation.currency)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Sous-total avec frais */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {t('realTimePricing.breakdown.subtotalWithFees')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatUnifiedPrice(pricing.subtotalWithFees, adaptedCalculation.currency)}
                    </Typography>
                  </Box>

                  {/* Caution */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography 
                      variant="body2" 
                      color={pricing.deposit > 0 ? "warning.main" : "text.secondary"} 
                      fontWeight="medium"
                    >
                      {t('realTimePricing.breakdown.deposit')}:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color={pricing.deposit > 0 ? "warning.main" : "text.secondary"} 
                      fontWeight="medium"
                    >
                      {pricing.deposit > 0 ? `+ ${formatUnifiedPrice(pricing.deposit, adaptedCalculation.currency)}` : '+ 0,00 DZD'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Total final */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="bold">
                      {t('realTimePricing.breakdown.totalToPay')}:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatUnifiedPrice(pricing.total, adaptedCalculation.currency)}
                    </Typography>
                  </Box>

                  {/* Information caution */}
                  {pricing.deposit > 0 && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 1.5, 
                      backgroundColor: 'warning.light', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'warning.main'
                    }}>
                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 'medium' }}>
                        💡 {t('realTimePricing.depositNote', { amount: formatUnifiedPrice(pricing.deposit, adaptedCalculation.currency) })}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

export default RealTimePricing;
